import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'lodash-es';

import { makeFontStyles, makeFontStyleStyles } from '@blaze-shared/elements-domain';

import { ProjectNavigateService } from '../../services/project-navigate.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { BreadcrumbsPathItem, BreadcrumbsResolverService } from './breadcrumbs-resolver.service';
import { DividerTypeOption, LabelStyle } from './breadcrumbs.models';

@Component({
  selector: 'be-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BreadcrumbsComponent
  extends CanvasElementBaseComponent<'breadcrumbs'>
  implements OnChanges
{
  private readonly navigationService = inject(ProjectNavigateService);
  private readonly breadcrumbsService = inject(BreadcrumbsResolverService);

  protected readonly drilldownPath = toSignal(this.breadcrumbsService.drilldownBreadcrumbs$);
  protected readonly labelStyle = signal<Record<string, string>>({});
  protected readonly dividerStyle = signal<Record<string, string>>({});
  protected readonly dividerType = signal<DividerTypeOption>('chevron');

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.offset?.margin);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this.updateLabelStyles();
      this.updateDividerStyles();
    }
  }

  onOpenPath(pathItem: BreadcrumbsPathItem, event: MouseEvent): void {
    event.preventDefault();
    const pageId = pathItem.pageId;
    if (!pageId) {
      return;
    }
    this.navigationService.navigateToPage({
      targetPageId: pageId,
      pageContext: pathItem.pageContext,
    });
  }

  private updateDividerStyles(): void {
    const { style } = this.props ?? {};
    if (!style) {
      return;
    }
    const dividerStyles = {
      color: style.divider.color.color1,
      '--icon-size': style.divider.size,
    };
    this.dividerType.set(style.divider.type);
    this.dividerStyle.set(dividerStyles);
  }

  private updateLabelStyles(): void {
    const { style } = this.props ?? {};
    if (!style) {
      return;
    }
    const states = ['hover', 'active', 'normal'] as const;

    const labelStyles = states.reduce(
      (acc, state) => {
        const stateStyle = this.getLabelStateStyles(style.label[state]);
        const vars = map(stateStyle, (value, key) => [
          `--breadcrumbs-label-${state}-${key}`,
          value,
        ]);
        return { ...acc, ...Object.fromEntries(vars) };
      },
      {} as Record<string, string>,
    );

    this.labelStyle.set(labelStyles);
  }

  private getLabelStateStyles(style: LabelStyle): Record<string, string> {
    return {
      color: style.color.color1,
      ...makeFontStyleStyles(style.style, '700'),
      ...makeFontStyles({
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        lineHeight: style.fontSize,
      }),
      ['font-weight']: this.fontsService.getFontWeight(style.fontFamily, style.fontWeight),
    };
  }
}
